/**
 * PowerplayOverrideField — pre-match Powerplay override widget.
 *
 * Closes CF-P1-04 audit finding. Renders alongside is_rain_affected;
 * shape depends on the match's format:
 *
 *   T20 / T10             →  {"powerplay_overs": <int>}
 *   ODI                   →  {"pp1":[start,end],"pp2":[...],"pp3":[...]}
 *   HUNDRED (2026-07-10)  →  {"windows":[{start_ball, end_ball, type}, ...],
 *                             "powerplay_overs": <int>}   ← mirror
 *
 * The Hundred shape was reworked 2026-07-10 to match the founder-
 * supplied UI (1.jpeg + 2.jpeg): a three-row Start ball / End ball /
 * Type table with Powerplay / Middle / Death type options. Storing a
 * `windows` array lets the operator carve up all three phases for a
 * rain-affected 100-ball innings; the legacy `powerplay_overs` int is
 * derived from the Powerplay window at save time so downstream
 * readers (analyzer/_phases.py, laws.py bowler cap, BBP selectors)
 * keep working without a data migration.
 *
 * Format is sourced from the sibling match_type select via the
 * matchTypeId prop. We fetch the match types list once to resolve the
 * id back to a format string. Test format does not have a Powerplay,
 * so the widget renders a no-op note for that case.
 *
 * The widget is "soft active" — it always allows editing, but renders
 * a muted banner when is_rain_affected is false so the operator knows
 * the values won't be applied until they flip the rain-affected
 * toggle. This matches the backend semantics (Match model line 218):
 * is_rain_affected=False → powerplay_override is ignored.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../api/axios";
import { ENDPOINTS } from "../../config/endpoints";


// Format → shape. Anything not in this map (Test, T100 default, ...)
// shows the "no Powerplay configurable" note.
const SHAPE = {
  T20:     "single",
  T10:     "single",
  HUNDRED: "hundred_windows",
  ODI:     "three_phase",
};


const HUNDRED_TYPE_OPTIONS = [
  { value: "",          label: "Select Type" },
  { value: "POWERPLAY", label: "Powerplay" },
  { value: "MIDDLE",    label: "Middle" },
  { value: "DEATH",     label: "Death" },
];


export default function PowerplayOverrideField({ value, onChange, matchTypeId, isActive }) {
  const [matchTypes, setMatchTypes] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get(ENDPOINTS.matches.matchTypes)
      .then((res) => {
        if (!alive) return;
        const raw = res.data?.results ?? res.data ?? [];
        setMatchTypes(Array.isArray(raw) ? raw : []);
      })
      .catch(() => { /* widget gracefully degrades to single shape */ });
    return () => { alive = false; };
  }, []);

  const formatCode = useMemo(() => {
    if (!matchTypeId) return null;
    const m = matchTypes.find(
      (mt) => mt.id === matchTypeId || mt.id === Number(matchTypeId),
    );
    // Match-type "name" / "code" varies by backend — try both.
    return (m?.code || m?.name || "").toString().toUpperCase().replace(/\s+/g, "");
  }, [matchTypeId, matchTypes]);

  const shape = SHAPE[formatCode] || "single";
  const isTest = formatCode === "TEST";

  if (isTest) {
    return (
      <div className="text-xs text-text-secondary px-3 py-2 rounded border border-gray-700 bg-input-secondary/60">
        Powerplay does not apply in Test format. Leave the rain-affected
        toggle off — the field will be ignored on save.
      </div>
    );
  }

  return (
    <div className={`rounded border p-3 space-y-2 ${
      isActive
        ? "border-amber-500/40 bg-amber-500/5"
        : "border-gray-700 bg-input-secondary/60"
    }`}>
      {!isActive && (
        <div className="text-[11px] text-text-secondary">
          Values below are saved but only applied when
          "Rain affected" is on. Toggle it above to activate.
        </div>
      )}

      {shape === "single" && (
        <SinglePhase value={value} onChange={onChange} formatCode={formatCode} />
      )}
      {shape === "three_phase" && (
        <ThreePhase value={value} onChange={onChange} />
      )}
      {shape === "hundred_windows" && (
        <HundredWindows value={value} onChange={onChange} />
      )}
    </div>
  );
}


function SinglePhase({ value, onChange, formatCode }) {
  const overs = value?.powerplay_overs ?? "";
  const label = "Powerplay overs";
  const placeholder = "e.g. 6 for T20";
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
        {label}
      </span>
      <input
        type="number"
        min={0}
        max={50}
        value={overs}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange({});
            return;
          }
          const n = Math.max(0, Math.min(50, Number(raw) || 0));
          onChange({ powerplay_overs: n });
        }}
        className="w-full py-2 px-3 h-[44px] bg-input-secondary border border-gray-700 rounded text-sm focus:outline-none focus:border-button-primary"
      />
    </label>
  );
}


function ThreePhase({ value, onChange }) {
  // ODI shape — three Powerplay windows, each a [start, end] over pair.
  // Defaults match ICC ODI playing conditions: PP1 0-10 (mandatory),
  // PP2 11-40 (max 4 fielders outside), PP3 41-50 (max 5 outside).
  const phases = [
    { key: "pp1", label: "PP1 (max 2 outside)", defaults: [0, 10] },
    { key: "pp2", label: "PP2 (max 4 outside)", defaults: [11, 40] },
    { key: "pp3", label: "PP3 (max 5 outside)", defaults: [41, 50] },
  ];

  const setPhase = (key, idx, raw) => {
    const next = { ...(value || {}) };
    const cur = Array.isArray(next[key]) ? [...next[key]] : [];
    const n = raw === "" ? null : Math.max(0, Math.min(50, Number(raw) || 0));
    cur[idx] = n;
    next[key] = cur;
    // Drop the key entirely if both bounds are blank — keeps the
    // payload clean so the serializer treats it as "use default for
    // this phase".
    if (cur[0] == null && cur[1] == null) delete next[key];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {phases.map((p) => {
        const cur = Array.isArray(value?.[p.key]) ? value[p.key] : [];
        const start = cur[0] ?? "";
        const end = cur[1] ?? "";
        return (
          <div key={p.key} className="grid grid-cols-[1fr_60px_60px] gap-2 items-center">
            <span className="text-[11px] text-text-secondary">{p.label}</span>
            <input
              type="number"
              min={0}
              max={50}
              placeholder={String(p.defaults[0])}
              value={start}
              onChange={(e) => setPhase(p.key, 0, e.target.value)}
              className="py-1.5 px-2 h-[36px] bg-input-secondary border border-gray-700 rounded text-sm font-mono focus:outline-none focus:border-button-primary"
            />
            <input
              type="number"
              min={0}
              max={50}
              placeholder={String(p.defaults[1])}
              value={end}
              onChange={(e) => setPhase(p.key, 1, e.target.value)}
              className="py-1.5 px-2 h-[36px] bg-input-secondary border border-gray-700 rounded text-sm font-mono focus:outline-none focus:border-button-primary"
            />
          </div>
        );
      })}
      <div className="text-[10px] text-text-secondary pt-1">
        Start / end overs per phase. Leave both blank to use the format
        default for that phase.
      </div>
    </div>
  );
}


// 2026-07-10 — Hundred-only row-based picker per founder-supplied
// reference (1.jpeg + 2.jpeg). Three rows of Start ball / End ball /
// Type. Types are the ECB phase names: Powerplay / Middle / Death.
// Ball numbers 1-100 (Hundred innings length). Legacy powerplay_overs
// mirror derived from the "Powerplay" row so downstream code paths
// keep working.
function makeEmptyHundredWindows(rows = 3) {
  return Array.from({ length: rows }, () => ({
    start_ball: null,
    end_ball:   null,
    type:       "",
  }));
}


function deriveHundredPowerplayOvers(windows) {
  if (!Array.isArray(windows) || windows.length === 0) return null;
  const primary = windows.find((w) => w.type === "POWERPLAY");
  if (!primary || primary.end_ball == null) return null;
  // Hundred stores balls; downstream still expects "sets" (5-ball
  // sets = "overs" in Hundred's internal model).
  return Math.max(0, Math.round(primary.end_ball / 5));
}


function HundredWindows({ value, onChange }) {
  const windows = Array.isArray(value?.windows) && value.windows.length > 0
    ? value.windows
    : makeEmptyHundredWindows(3);

  const setRow = (idx, field, raw) => {
    const next = windows.map((w) => ({
      start_ball: w.start_ball ?? null,
      end_ball:   w.end_ball   ?? null,
      type:       w.type       || "",
    }));
    if (field === "type") {
      next[idx].type = raw;
    } else {
      const n = raw === "" ? null : Math.max(1, Math.min(100, Number(raw) || 0));
      next[idx][field] = n;
    }
    // Drop trailing all-blank rows before persisting so the payload
    // reads clean; keep at least one row so the widget renders.
    const trimmed = [...next];
    while (
      trimmed.length > 1 &&
      trimmed[trimmed.length - 1].start_ball == null &&
      trimmed[trimmed.length - 1].end_ball == null &&
      !trimmed[trimmed.length - 1].type
    ) {
      trimmed.pop();
    }
    const merged = { ...(value || {}), windows: trimmed };
    const pp = deriveHundredPowerplayOvers(trimmed);
    if (pp != null) merged.powerplay_overs = pp;
    else delete merged.powerplay_overs;
    onChange(merged);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_1fr_2fr] gap-2 text-[10px] uppercase tracking-wider text-text-secondary">
        <span>Start ball</span>
        <span>End ball</span>
        <span>Type</span>
      </div>
      {windows.map((row, idx) => (
        <div key={idx} className="grid grid-cols-[1fr_1fr_2fr] gap-2 items-center">
          <input
            type="number" min={1} max={100}
            placeholder="1-100"
            value={row.start_ball ?? ""}
            onChange={(e) => setRow(idx, "start_ball", e.target.value)}
            className="py-1.5 px-2 h-[36px] bg-input-secondary border border-gray-700 rounded text-sm font-mono focus:outline-none focus:border-button-primary"
          />
          <input
            type="number" min={1} max={100}
            placeholder="1-100"
            value={row.end_ball ?? ""}
            onChange={(e) => setRow(idx, "end_ball", e.target.value)}
            className="py-1.5 px-2 h-[36px] bg-input-secondary border border-gray-700 rounded text-sm font-mono focus:outline-none focus:border-button-primary"
          />
          <select
            value={row.type || ""}
            onChange={(e) => setRow(idx, "type", e.target.value)}
            className="py-1.5 px-2 h-[36px] bg-input-secondary border border-gray-700 rounded text-sm focus:outline-none focus:border-button-primary"
          >
            {HUNDRED_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-black text-white">
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ))}
      <div className="text-[10px] text-text-secondary pt-1">
        Hundred phases are ball-numbered (1-100). ECB default:
        Powerplay 1-25, Middle 26-75, Death 76-100. Adjust for a
        rain-shortened innings.
      </div>
    </div>
  );
}
